import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { FieldArray } from 'react-final-form-arrays';
import { Form, FormSpy } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import { get } from 'lodash';
import { TableProps } from './types';
import {
  TableData,
  TableHeaders,
  NoResultRow,
  RowsContainer,
  TableTitle,
} from '../SharedTable/styles';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import useTable from '../utils/useTable';
import TableCell from '../SharedTable/components/TableCell';

const EditTable = <T,>(props: TableProps<T>) => {
  const {
    headers,
    noResultsText = 'No obtuvimos resultados para tu búsqueda :(',
    rowCount = 5,
    size = 'small',
    dynamicHeight = false,
    name = 'name',
    form,
    formState,
    schema,
    fontSize = 'small',
  } = props;

  const data = useMemo(
    () => (get(formState.initialValues, name) ? get(formState.initialValues, name) : []),
    [formState.initialValues, name],
  );

  const { actualTheme } = useContext(ThemeContext);
  const [disableAllCheck, setDisableAllCheck] = useState(true);

  const { rowsContainerRef, renderEditableCellContent, rowProps } = useTable<T>({
    ...props,
    disableAllCheck,
    setDisableAllCheck,
    data,
  });

  const renderHeaders = useCallback(
    () => (
      <TableHeaders {...rowProps} size={size} dynamicHeight={dynamicHeight} fontSize={fontSize}>
        {headers.map((header, i) => (
          <TableCell header={header} index={i}>
            <TableTitle>{header.label}</TableTitle>
          </TableCell>
        ))}
      </TableHeaders>
    ),
    [rowProps, size, dynamicHeight, fontSize, headers],
  );

  const renderRows = useCallback(
    () => (
      <Form
        // eslint-disable-next-line react/jsx-no-bind
        onSubmit={() => {}}
        initialValues={formState.initialValues}
        mutators={{
          ...arrayMutators,
        }}
        validate={schema}
      >
        {({ values }) => (
          <form>
            <FormSpy
              subscription={{
                dirty: true,
                values: true,
                invalid: true,
              }}
              // eslint-disable-next-line react/jsx-no-bind
              onChange={(tableForm) => {
                form.change(name, get(tableForm.values, name));
              }}
            />
            <FieldArray name={name}>
              {({ fields }) =>
                fields.map((fieldName, index) => (
                  <div role="presentation">
                    <TableData
                      {...rowProps}
                      isLastRow={false}
                      size={size}
                      dynamicHeight={dynamicHeight}
                      fontSize={fontSize}
                    >
                      {headers.map((header) => (
                        <TableCell header={header} index={index}>
                          {header.type
                            ? renderEditableCellContent[header.type](
                                header,
                                get(values, name)[index],
                                index,
                                fieldName,
                              )
                            : renderEditableCellContent.string(
                                header,
                                get(values, name)[index],
                                index,
                              )}
                        </TableCell>
                      ))}
                    </TableData>
                  </div>
                ))
              }
            </FieldArray>
          </form>
        )}
      </Form>
    ),
    [
      formState.initialValues,
      schema,
      name,
      form,
      rowProps,
      size,
      dynamicHeight,
      fontSize,
      headers,
      renderEditableCellContent,
    ],
  );

  useEffect(() => {
    form.registerField(name, () => {}, {
      active: true,
      dirty: true,
      touched: true,
      valid: true,
      value: true,
    });
  }, [form, name]);

  return (
    <ThemeProvider theme={actualTheme}>
      {renderHeaders()}
      {data.length > 0 ? (
        <RowsContainer rowCount={rowCount} ref={rowsContainerRef} size={size} fontSize={fontSize}>
          {renderRows()}
        </RowsContainer>
      ) : (
        <NoResultRow {...rowProps} hover={false} size={size} fontSize={fontSize}>
          {noResultsText}
        </NoResultRow>
      )}
    </ThemeProvider>
  );
};

export default EditTable;
